import React from 'react';
import { Flow } from '@dynamic-flow/types';
import {
  resolveTheme,
  generateCSSVariables,
  generateAdvancedCSSVariables,
} from '@dynamic-flow/ui';

interface BindingErrorProps {
  flow: Flow;
  message: string;
  isPreview?: boolean;
}

type BindingErrorIconSize = 'sm' | 'lg';

const ICON_SIZE_CLASSES: Record<
  BindingErrorIconSize,
  { outer: string; inner: string }
> = {
  sm: { outer: 'w-5 h-5', inner: 'w-5 h-5' },
  lg: { outer: 'w-16 h-16 mx-auto', inner: 'w-8 h-8' },
};

function BindingErrorIcon({ size = 'lg' }: { size?: BindingErrorIconSize }) {
  const { outer, inner } = ICON_SIZE_CLASSES[size];

  return (
    <div
      className={`form-binding-error-icon-outer ${outer} rounded-full flex items-center justify-center`}
    >
      <svg
        className={`${inner} form-binding-error-icon-inner`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

export const BindingError: React.FC<BindingErrorProps> = ({
  flow,
  message,
  isPreview = false,
}) => {
  const theme = resolveTheme(flow.theme);
  const themeCSS = generateCSSVariables(theme);
  const advancedThemeCSS = generateAdvancedCSSVariables(
    flow.theme?.advanced ?? {}
  );
  const mergedCSS = `${themeCSS}\n${advancedThemeCSS}`;

  if (isPreview) {
    // Preview mode - show error banner at top
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: mergedCSS }} />
        <div className="form-binding-error-banner">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <BindingErrorIcon size="sm" />
              </div>
              <div className="flex-1">
                <h3 className="form-binding-error-title">
                  URL Configuration Error
                </h3>
                <p className="form-binding-error-text">{message}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Public mode - show centered error box
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: theme.colors.background.default }}
    >
      <style dangerouslySetInnerHTML={{ __html: mergedCSS }} />
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center border">
          <div className="mb-6">
            <div className="my-2">
              <BindingErrorIcon />
            </div>
            <h1
              className="text-xl font-semibold mb-2"
              style={{ color: theme.colors.text.primary }}
            >
              Invalid URL
            </h1>
            <p
              className="text-sm mb-6"
              style={{ color: theme.colors.text.secondary }}
            >
              {message}
            </p>
          </div>

          <div
            className="text-xs p-4 rounded-md"
            style={{
              backgroundColor: theme.colors.background.accent,
              color: theme.colors.text.secondary,
            }}
          >
            <p className="font-medium mb-2">What to do:</p>
            <ul className="text-left space-y-1">
              <li>• Check that you have the complete form URL</li>
              <li>• Contact the form administrator</li>
              <li>• Verify all required parameters are included</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
